import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, CheckCircle, XCircle, Clock, Shield, ArrowRight, FileText } from 'lucide-react';
import { kycService } from '@services/kycService';

const KycHistoryPage = () => {
    const { clientId, kycId } = useParams();
    const navigate = useNavigate();

    const [kycDocument, setKycDocument] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchData();
    }, [kycId]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [docResponse, historyResponse] = await Promise.all([
                kycService.clientKyc.getById(kycId),
                kycService.clientKyc.getHistory(kycId)
            ]);

            if (docResponse.success && docResponse.data) {
                setKycDocument(docResponse.data);
            }
            if (historyResponse.success && historyResponse.data) {
                setHistory(historyResponse.data);
            }
        } catch (err) {
            console.error('Error fetching KYC history:', err);
            setError('Failed to load history');
        } finally {
            setLoading(false);
        }
    };

    const getStatusConfig = (status) => {
        switch (status) {
            case 'APPROVED':
                return { color: 'text-green-600', bg: 'bg-green-100', icon: CheckCircle, label: 'Approved' };
            case 'VERIFIED':
                return { color: 'text-blue-600', bg: 'bg-blue-100', icon: Shield, label: 'Verified' };
            case 'REJECTED':
                return { color: 'text-red-600', bg: 'bg-red-100', icon: XCircle, label: 'Rejected' };
            case 'PENDING':
                return { color: 'text-yellow-600', bg: 'bg-yellow-100', icon: Clock, label: 'Pending' };
            default:
                return { color: 'text-gray-600', bg: 'bg-gray-100', icon: Clock, label: status || 'Unknown' };
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white p-4 rounded-lg shadow-sm">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate(`/clients/${clientId}`, { state: { activeTab: 'kyc' } })}
                        className="p-2 hover:bg-gray-100 rounded-lg transition"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <h2 className="text-2xl font-semibold text-gray-900">Document History</h2>
                        {kycDocument && (
                            <p className="text-sm text-gray-500 mt-1">
                                {kycDocument.requirementName || kycDocument.kycRequirement?.name || 'KYC Document'}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                    {error}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Document Info Card */}
                {kycDocument && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                            <FileText className="w-5 h-5" />
                            Document Info
                        </h3>
                        <div className="space-y-3">
                            <div>
                                <p className="text-xs text-gray-500">Requirement</p>
                                <p className="text-sm text-gray-900 font-medium">
                                    {kycDocument.requirementName || kycDocument.kycRequirement?.name || '-'}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500">Document Number</p>
                                <p className="text-sm text-gray-900">{kycDocument.documentNumber || '-'}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500">Status</p>
                                <span className={`inline-flex px-2 py-1 text-xs rounded-full ${getStatusConfig(kycDocument.status).bg} ${getStatusConfig(kycDocument.status).color}`}>
                                    {getStatusConfig(kycDocument.status).label}
                                </span>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500">Version</p>
                                <p className="text-sm text-gray-900">v{kycDocument.version || 1}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500">Submitted</p>
                                <p className="text-sm text-gray-900">{formatDate(kycDocument.submittedAt || kycDocument.createdAt)}</p>
                            </div>
                            {kycDocument.documentUrl && (
                                <a
                                    href={kycDocument.documentUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition"
                                >
                                    <FileText className="w-4 h-4" />
                                    View File
                                </a>
                            )}
                            {kycDocument.status === 'REJECTED' && kycDocument.rejectionReason && (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                    <p className="text-xs text-red-600 font-medium mb-1">Rejection Reason</p>
                                    <p className="text-sm text-red-800">{kycDocument.rejectionReason}</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Timeline */}
                <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-6">Status Timeline</h3>

                    {history.length === 0 ? (
                        <div className="text-center py-8">
                            <Clock className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500">No history records found</p>
                        </div>
                    ) : (
                        <div className="relative">
                            {/* Timeline line */}
                            <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-200" />

                            <div className="space-y-6">
                                {history.map((entry, index) => {
                                    const toConfig = getStatusConfig(entry.toStatus || entry.status);
                                    const StatusIcon = toConfig.icon;

                                    return (
                                        <div key={entry.id || index} className="relative flex gap-4">
                                            {/* Timeline dot */}
                                            <div className={`relative z-10 flex-shrink-0 w-10 h-10 rounded-full ${toConfig.bg} flex items-center justify-center`}>
                                                <StatusIcon className={`w-5 h-5 ${toConfig.color}`} />
                                            </div>

                                            {/* Content */}
                                            <div className="flex-1 bg-gray-50 rounded-lg p-4">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    {entry.fromStatus && (
                                                        <>
                                                            <span className="text-sm font-medium text-gray-500">
                                                                {entry.fromStatus}
                                                            </span>
                                                            <ArrowRight className="w-3 h-3 text-gray-400" />
                                                        </>
                                                    )}
                                                    <span className={`text-sm font-semibold ${toConfig.color}`}>
                                                        {entry.toStatus || entry.status}
                                                    </span>
                                                </div>

                                                <p className="text-xs text-gray-500 mt-1">
                                                    {formatDate(entry.createdAt || entry.timestamp)}
                                                </p>

                                                {entry.performedBy && (
                                                    <p className="text-xs text-gray-500 mt-0.5">
                                                        By: {entry.performedBy}
                                                    </p>
                                                )}

                                                {(entry.comment || entry.reason) && (
                                                    <div className="mt-2 bg-white rounded-lg p-3 border border-gray-200">
                                                        <p className="text-sm text-gray-700">
                                                            {entry.comment || entry.reason}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default KycHistoryPage;
